import { ITag, TagType } from "dto/ITag";
import {
  MutableRefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import CaseOptionsCard from "components/CaseOptions";
import CaseStatusIcons from "components/CaseStatusIcons";
import CaseThreadsModal from "components/CaseThreadsModal";
import Chip from "components/Chip";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import CasesContext from "contexts/CasesContext";
import MenuContext from "contexts/MenuContext";
import settingsContext, { IOptionType } from "contexts/SettingsContext";
import { useNavigate, useParams } from "react-router-dom";
import sanitizeHtml from "sanitize-html";
import StatusModal from "components/StatusModal";
import Popper from "components/Popper";
import { IStatus } from "dto/IStatus";
import ChooseTagNew from "components/ChooseTagNew";
import useCurrentCompanyId from "hooks/useCurrentCompanyId";
import PassCasesModal from "components/PassCasesModal";
import { ICaseSimple } from "dto/Cases/ICaseSimple";

const CaseCard: FC<Props> = ({
  c,
  tags,
  statuses,
  setSelectedEdit,
  className = "",
  onClick,
  isSelectable = false,
  selectMultiple = false,
  selectedElements,
  setSelectedElements,
  setSelectedThread,
  setCreateNewThread,
  separatorClass,
  setForId,
  searchText,
  inDeskView,
}) => {
  const navigate = useNavigate();

  const { caseId } = useParams();
  const [findedTags, setFindedTags] = useState<ITag[]>([]);
  const [showOptions, setShowOptions] = useState(false);
  const { cases, setCases } = useContext(CasesContext);
  const refWrapper = useRef(null);
  const [showThreads, setShowThreads] = useState(false);

  const { id } = useContext(MenuContext);
  const [status, setStatus] = useState<IStatus | null>(null);
  const [showEditTags, setShowEditTags] = useState(false);
  const [showEditStatus, setShowEditStatus] = useState(false);
  // „Przekaż" z menu teczki — modal żyje tu, nie w CaseOptionsCard, bo menu z prawego
  // kliknięcia odmontowuje kartę opcji zaraz po wyborze pozycji (jak StatusModal wyżej).
  const [casesToPass, setCasesToPass] = useState<ICaseSimple[] | null>(null);

  const [statusPopperRef] =
    useState<MutableRefObject<HTMLDivElement>>(null);
  const [tagPopperRef] =
    useState<MutableRefObject<HTMLDivElement>>(null);

  useEffect(() => {
    if (id !== c.Id) {
      setShowOptions(false);
    }
  }, [id]);

  const handleStatusUpdate = (status?: IStatus) => {
    setStatus(status);
    setShowEditStatus(false);
  };

  const handleUpdateTags = (tags: ITag[]) => {
    const newCases = [...cases];

    const el = newCases.find((cc) => c.Id === cc.Id);

    if (el) {
      el.TagIds = tags.map((t) => t.Id);
    }

    setCases(newCases);
  };


  useEffect(() => {
    const t = tags?.filter((tag) => c?.TagIds?.find((iTag) => tag.Id === iTag));
    setFindedTags(t);

    const newStatus = statuses?.find((s) => s.Id === c?.StatusId);

    setStatus(newStatus ?? null);
  }, [statuses, c.StatusId, c.TagIds]);

  const currentCompanyId = useCurrentCompanyId();

  const isSelected = !!selectedElements?.some((s) => s.Id === c.Id);

  // Zaznaczanie wielu teczek (jak MessageCard na liście przesyłek): Ctrl+klik przełącza
  // zaznaczenie, Shift+klik zaznacza zakres od ostatnio zaznaczonej do klikniętej
  // (w kolejności listy). Zaznaczone teczki obsługuje pasek nad listą (CaseList).
  const handleSelect = (multiple: boolean) => {
    if (!setSelectedElements) return;

    const current = selectedElements ?? [];

    if (multiple && current.length > 0) {
      const last = current[current.length - 1];
      const from = cases.findIndex((x) => x.Id === last.Id);
      const to = cases.findIndex((x) => x.Id === c.Id);

      if (from !== -1 && to !== -1) {
        const [start, end] = from < to ? [from, to] : [to, from];
        const range = cases.slice(start, end + 1);
        const merged = [...current, ...range].filter(
          (item, index, self) =>
            index === self.findIndex((o) => o.Id === item.Id),
        );

        setSelectedElements(merged);
        return;
      }
    }

    if (isSelected) {
      setSelectedElements(current.filter((s) => s.Id !== c.Id));
      return;
    }

    setSelectedElements([...current, c]);
  };

  const handleClick = (e: any) => {
    const ele = e.target.tagName.toLowerCase();

    if (ele !== "div") {
      return;
    }

    if (e.target.className.search("button") !== -1) {
      return;
    }

    if (isSelectable || e.ctrlKey || e.metaKey || e.shiftKey) {
      handleSelect(selectMultiple || !!e.shiftKey);
      return;
    }

    const newCases = [...cases];
    const el = newCases.find((p) => p.Id === c.Id);
    if (el) {
      el.Unread = false;
      setCases(newCases);
    }

    if (caseId !== c.Id) {
      // navigate(`/packages/${filterId}/${linkStatus}/${c.Kind}/${c.Id}`);
      if (c.PartialShare) {
        navigate(`/cases/${c.Id}/partial`);
        return;
      }
      localStorage.setItem(`last_case_${currentCompanyId}`, c.Id);
      navigate(`/cases/${c.Id}/chat`);
    } else {
      navigate(`/cases`);
    }
  };

  useEffect(() => {}, [c, c.Title]);

  const { options } = useContext(settingsContext);

  const h = options.find((f) => f.Type === IOptionType.hoverMenu)?.Value;

  const useHoverOver = h === "true";

  useEffect(() => {
    if (c.Id === caseId) {
      setShowOptions(true);
      return;
    }
    setShowOptions(false);
  }, [caseId]);

  const [isRightClicked, setIsRightClicked] = useState(false);

  const handleClickRightButton = () => {
    if (c.Id === caseId) return;
    setShowOptions(true);
    setIsRightClicked(true);
  };

  const rippleContainerRef = useRef(null);

  const createRipple = (event: MouseEvent) => {
    const container = rippleContainerRef.current as HTMLElement;
    if (!container) return;

    // Usuń poprzedni ripple, jeśli istnieje
    const oldRipple = container.querySelector(`.${styles.ripple}`);
    if (oldRipple) {
      oldRipple.remove();
    }

    // Utwórz nowy element ripple
    const ripple = document.createElement("span");
    const diameter = Math.max(container.clientWidth, container.clientHeight);
    const radius = diameter / 2;

    ripple.style.width = ripple.style.height = `${diameter}px`;
    ripple.style.left = `${event.clientX - container.offsetLeft - radius}px`;
    ripple.style.top = `${event.clientY - container.offsetTop - radius}px`;
    ripple.classList.add(styles.ripple);

    // Dodaj ripple do kontenera
    container.appendChild(ripple);

    // Usuń ripple po zakończeniu animacji (600ms)
    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  return (
    <div
      data-id={c.Id}
      ref={rippleContainerRef}
      onClick={(e) => {
        createRipple(e);
      }}
      className={`${styles.rippleContainer}`}
    >
      <div
        onContextMenu={(e) => {
          e.preventDefault();
          handleClickRightButton();
        }}
        data-id={c.Id}
        key={c.Id}
        onClick={
          onClick
            ? (e: any) => {
                const ele = e.target.tagName.toLowerCase();
                const button = e.target.outerHTML.search("button") !== -1;

                if (ele !== "div" || button) {
                  return;
                }

                setForId(c.Id);
                onClick?.();
              }
            : handleClick
        }
        className={`${styles.card} ${
          caseId === c.Id && styles.active
        } ${isSelected ? styles.selected : ""} ${className} `}
        ref={refWrapper}
      >
        <div className={`${styles.inner}`}>
          {refWrapper.current && showOptions && (
            <CaseOptionsCard
              showDropdown={useHoverOver ? false : true}
              showOptions={showOptions}
              showSomething={true}
              pack={c}
              refetch={() => {}}
              refWrapper={refWrapper}
              onClickEdit={() => setSelectedEdit(c)}
              onClickAttachIcon={() => {}}
              toggle={() => {
                setShowOptions(false);
                setIsRightClicked(false);
              }}
              setShowThreads={setShowThreads}
              showThreads={showThreads}
              selectedElements={selectedElements}
              setSelectedElements={setSelectedElements}
              dropdown={isRightClicked}
              isRightClicked={isRightClicked}
              setShowEditStatus={setShowEditStatus}
              setShowEditTags={setShowEditTags}
              showEditStatus={showEditStatus}
              showEditTags={showEditTags}
              onPass={(list) => setCasesToPass(list)}
            />
          )}

          {casesToPass && (
            <PassCasesModal
              cases={casesToPass}
              onClose={() => setCasesToPass(null)}
            />
          )}

          {c.Unread && <div className={styles.colorBar}></div>}
          <div className={styles.header}>
            <div
              className={styles.title}
              dangerouslySetInnerHTML={{ __html: c.Title }}
            ></div>
            <div className={styles.icons}>
              <CaseStatusIcons
                Case={cases.find((a) => a.Id === c.Id)}
                inList={true}
                searchText={searchText}
                inDeskView={inDeskView}
                statuses={statuses}
              />
            </div>
          </div>

          <div
            className={styles.client}
            dangerouslySetInnerHTML={{ __html: c?.Client ?? "" }}
          ></div>

          <div className={styles.signatureWrapper}>
            <div className={styles.signature}>
              {!!findedTags.length && (
                <div className={styles.tags} ref={tagPopperRef}>
                  {!c.Tags &&
                    findedTags.map((tag, i) => (
                      <Chip
                        key={`chip_${i}`}
                        small
                        element={<>{tag.Name}</>}
                        // onDelete={() => handleRemoveTag(tag)}
                      />
                    ))}
                  {c.Tags &&
                    c.Tags.map((tag, i) => (
                      <Chip
                        key={`tag_small_${i}`}
                        small
                        element={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(tag.Name),
                            }}
                          ></span>
                        }
                      />
                    ))}
                </div>
              )}

              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(c?.Signature ?? ""),
                }}
              ></div>
            </div>
          </div>
        </div>

        {showThreads && (
          <ModalLayout
            dock={DockPosition.BottomRight}
            onEnd={() => {
              setShowThreads(false);
            }}
          >
            <CaseThreadsModal
              setCreateNewThread={() => setCreateNewThread?.(true)}
              toggle={setShowThreads}
              onSelectThread={(t) => {
                setSelectedThread?.(t);
              }}
            />
          </ModalLayout>
        )}

        {showEditStatus && (
          <Popper
            anchorEl={statusPopperRef?.current ?? refWrapper?.current}
            open={showEditStatus}
            setOpen={setShowEditStatus}
          >
            <StatusModal
              item={c}
              toggle={setShowEditStatus}
              selectedStatus={status}
              afterUpdate={handleStatusUpdate}
            />
          </Popper>
        )}
        {showEditTags && (
          <Popper
            anchorEl={tagPopperRef?.current ?? refWrapper?.current}
            open={showEditTags}
            setOpen={setShowEditTags}
          >
            <ChooseTagNew
              toTag={c}
              toggle={setShowEditTags}
              updateTags={handleUpdateTags}
              type={TagType.Cases}
            />
          </Popper>
        )}

        <div className={`${styles.separator} ${separatorClass}`}></div>
      </div>
    </div>
  );
};

export default CaseCard;
